import { mergeClasses } from '@fluentui/react-components';
import { ChevronLeft16Regular, ChevronRight16Regular } from '@fluentui/react-icons';
import { useTranslation } from 'react-i18next';
import { BasicButton } from '@components/common/BasicButton';
import { useBookingsPaginationStyles } from './BookingsPagination.styles';
import type { BookingsPaginationViewModel } from '@/types/events.types';

interface BookingsPaginationProps {
  currentPage: number;
  pageCount: number;
  onPageChange: (page: number) => void;
}

const MAX_VISIBLE_PAGES = 4;

function buildViewModel(
  currentPage: number,
  pageCount: number,
  ariaLabel: string
): BookingsPaginationViewModel {
  const safePageCount = Math.max(pageCount, 1);
  const safeCurrentPage = Math.min(Math.max(currentPage, 1), safePageCount);
  const startPage = Math.min(safeCurrentPage, Math.max(1, safePageCount - MAX_VISIBLE_PAGES + 1));
  const endPage = Math.min(safePageCount, startPage + MAX_VISIBLE_PAGES - 1);
  const visiblePages = Array.from(
    { length: endPage - startPage + 1 },
    (_, index) => startPage + index
  );

  return {
    currentPage: safeCurrentPage,
    pageCount: safePageCount,
    visiblePages,
    canGoPrevious: safeCurrentPage > 1,
    canGoNext: safeCurrentPage < safePageCount,
    ariaLabel,
  };
}

export const BookingsPagination = ({
  currentPage,
  pageCount,
  onPageChange,
}: BookingsPaginationProps) => {
  const styles = useBookingsPaginationStyles();
  const { t } = useTranslation('dashboard');

  if (pageCount <= 1) {
    return null;
  }

  const viewModel = buildViewModel(currentPage, pageCount, t('pagination.ariaLabel'));

  return (
    <div className={styles.root}>
      <nav aria-label={viewModel.ariaLabel} className={styles.nav}>
        <BasicButton
          variant="transparent"
          size="small"
          aria-label={t('pagination.previous')}
          disabled={!viewModel.canGoPrevious}
          className={styles.navButton}
          icon={<ChevronLeft16Regular />}
          onClick={() => onPageChange(viewModel.currentPage - 1)}
        />

        <div className={styles.pageList}>
          {viewModel.visiblePages.map((page) => {
            const isActive = page === viewModel.currentPage;

            return (
              <BasicButton
                key={page}
                variant="transparent"
                size="small"
                aria-current={isActive ? 'page' : undefined}
                aria-label={t('pagination.page', { page })}
                className={mergeClasses(styles.pageButton, isActive && styles.activePage)}
                onClick={() => onPageChange(page)}
              >
                {page}
              </BasicButton>
            );
          })}
        </div>

        <BasicButton
          variant="transparent"
          size="small"
          aria-label={t('pagination.next')}
          disabled={!viewModel.canGoNext}
          className={styles.navButton}
          icon={<ChevronRight16Regular />}
          onClick={() => onPageChange(viewModel.currentPage + 1)}
        />
      </nav>
    </div>
  );
};

BookingsPagination.displayName = 'BookingsPagination';
